import { useState, useRef, useEffect } from "react";
import {
  FaUserTie,
  FaGraduationCap,
  FaAward,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";
import NarendraOjha from "../../assets/images/teachers/dr-narendra-ojha.jpeg";
import RameshUpadhyay from "../../assets/images/teachers/ramesh-upadhyay.jpeg";
import MrManojKumar from "../../assets/images/teachers/mr-manoj-kumar.png";
import RashmiSatyarthi from "../../assets/images/teachers/rashmi-satyarthi.png";

const FacultyCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef(null);
  const timeoutRef = useRef(null);

  const faculty = [
    {
      id: 1,
      name: "Mr Manoj Kumar",
      position: "Founder, Chairman & Director",
      department: "Management",
      qualification: "M.Sc (Chemistry)",
      experience: null,
      image: MrManojKumar,
      badgeColor: "bg-purple-100 text-purple-700",
      icon: FaUserTie,
    },
    {
      id: 2,
      name: "Rashmi Satyarthi",
      position: "Co-Ordinator",
      department: "Management",
      qualification: "B.Sc, B.Ed",
      experience: null,
      image: RashmiSatyarthi,
      badgeColor: "bg-purple-100 text-purple-700",
      icon: FaUserTie,
    },
    {
      id: 4,
      name: "Ramesh Upadhyay",
      position: "Vice-Principle",
      department: "IT",
      qualification: "M.Sc (English honours)",
      experience: "15+ Years",
      image: RameshUpadhyay,
      badgeColor: "bg-indigo-100 text-indigo-700",
      icon: FaGraduationCap,
    },
  ];

  // Create infinite loop by duplicating items
  const infiniteFaculty = [...faculty, ...faculty, ...faculty];

  // Check if mobile on resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const itemsPerPage = isMobile ? 1 : 4;
  const totalSlides = faculty.length;

  // Reset to beginning when reaching the end (infinite effect)
  useEffect(() => {
    if (currentIndex >= totalSlides) {
      // Jump back to start without animation
      timeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(0);
      }, 300);
    } else if (currentIndex < 0) {
      // Jump to end without animation
      timeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(totalSlides - 1);
      }, 300);
    }
  }, [currentIndex, totalSlides]);

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const nextSlide = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const prevSlide = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev - 1);
  };

  // Calculate transform value
  const getTransformValue = () => {
    const slideWidth = isMobile ? 100 : 25; // 100% for mobile, 25% for desktop (4 items)
    return `translateX(-${currentIndex * slideWidth}%)`;
  };

  return (
    <section className="py-12 bg-white">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto mb-8 text-center">
          <div className="inline-flex items-center px-3 py-1 mb-3 text-xs font-medium text-purple-600 rounded-full bg-purple-50">
            <FaUserTie className="w-3 h-3 mr-1.5" />
            Our Faculty
          </div>
          <h2 className="mb-3 text-2xl font-bold text-gray-900">
            Meet Our Expert Faculty
          </h2>
          <p className="text-sm text-gray-600">
            Dedicated educators shaping young minds with passion and expertise
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative px-8 mb-8">
          {/* Previous Button */}
          <button
            onClick={prevSlide}
            className="absolute left-0 z-10 flex items-center justify-center w-10 h-10 text-gray-600 transition-all duration-300 -translate-y-1/2 bg-white border border-gray-300 rounded-full shadow-lg top-1/2 hover:bg-gray-50 hover:border-gray-400 hover:scale-110"
            aria-label="Previous slide"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>

          {/* Carousel Track */}
          <div className="overflow-hidden">
            <div
              ref={carouselRef}
              className={`flex ${isTransitioning ? 'transition-transform duration-300 ease-in-out' : ''}`}
              style={{
                transform: getTransformValue(),
              }}
            >
              {infiniteFaculty.map((member, index) => (
                <div
                  key={`${member.id}-${index}`}
                  className={`flex-shrink-0 px-2 ${
                    isMobile ? 'w-full' : 'w-1/4'
                  }`}
                >
                  <div className="relative overflow-hidden transition-all duration-300 bg-white border border-gray-100 shadow-md group rounded-xl hover:shadow-xl hover:-translate-y-1">
                    {/* Image Container */}
                    <div className="relative overflow-hidden h-52">
                      <img
                        alt={member.name}
                        className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                        src={member.image}
                      />
                      {/* Experience Badge (if exists) */}
                      {member.experience && (
                        <div className="absolute top-3 right-3">
                          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 backdrop-blur-sm">
                            <FaAward className="w-3 h-3 text-amber-500" />
                            <span className="text-xs font-medium text-gray-700">
                              {member.experience}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      {/* Department Badge */}
                      <div className="mb-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-[10px] ${member.badgeColor}`}
                        >
                          {member.department}
                        </span>
                      </div>

                      {/* Name */}
                      <h3 className="mb-1 text-lg font-bold text-gray-900">
                        {member.name}
                      </h3>

                      {/* Position */}
                      <div className="mb-3 text-sm text-gray-600">
                        {member.position}
                      </div>

                      {/* Qualification */}
                      <div className="flex items-start gap-2">
                        <FaGraduationCap className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-700">
                          {member.qualification}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Next Button */}
          <button
            onClick={nextSlide}
            className="absolute right-0 z-10 flex items-center justify-center w-10 h-10 text-gray-600 transition-all duration-300 -translate-y-1/2 bg-white border border-gray-300 rounded-full shadow-lg top-1/2 hover:bg-gray-50 hover:border-gray-400 hover:scale-110"
            aria-label="Next slide"
          >
            <FaArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Dots Navigation */}
        <div className="flex justify-center gap-2 mt-6">
          {faculty.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setIsTransitioning(true);
                setCurrentIndex(index);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                currentIndex % faculty.length === index
                  ? "w-8 bg-purple-600"
                  : "bg-gray-300 hover:bg-gray-400"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FacultyCarousel;